import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { ArrowRight } from "lucide-react";

const AdminLogin = () => {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim(), password });
      localStorage.setItem("tenjara_token", data.access_token);
      localStorage.setItem("tenjara_user", JSON.stringify(data.user));
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || t("admin.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <header className="border-b border-charcoal/10">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3" data-testid="login-brand">
            <span className="font-heading text-2xl tracking-tight">Tenjara</span>
            <span className="overline">Lombok</span>
          </Link>
          <div className="flex items-center text-[0.72rem] tracking-[0.2em] uppercase">
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-1 ${lang === "en" ? "text-charcoal" : "text-charcoal/35"}`}
            >
              EN
            </button>
            <span className="text-charcoal/20">/</span>
            <button
              onClick={() => setLang("es")}
              className={`px-2 py-1 ${lang === "es" ? "text-charcoal" : "text-charcoal/35"}`}
            >
              ES
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <p className="overline" data-testid="login-overline">— Tenjara Admin</p>
          <h1 className="mt-4 font-heading text-5xl font-light tracking-tight text-charcoal">
            {t("admin.login_title")}
          </h1>
          <p className="mt-3 text-volcanic font-light">{t("admin.login_sub")}</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="login-form">
            <div>
              <label className="overline block mb-2" htmlFor="l-email">
                {t("admin.email")}
              </label>
              <input
                id="l-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="editorial-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="overline block mb-2" htmlFor="l-password">
                {t("admin.password")}
              </label>
              <input
                id="l-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="editorial-input"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive font-medium" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="group inline-flex items-center gap-3 bg-charcoal text-ivory px-8 py-4 text-[0.72rem] tracking-[0.22em] uppercase font-medium hover:bg-teakwood transition-colors duration-500 disabled:opacity-60"
            >
              {loading ? t("admin.sign_in_loading") : t("admin.sign_in")}
              <ArrowRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminLogin;
